import type { ClinicalNeed, NeedId } from '../types/filtek'
import { imagery } from './imagery'
import { brand } from './palette'

export const needs: ClinicalNeed[] = [
  {
    id: 'aesthetic',
    index: '01',
    label: 'Aesthetic control',
    demand: 'The case is visible, the patient is watching, and the margin has nowhere to hide.',
    resolution: 'Full command of opacity, translucency and polish — layer by layer.',
    technique: 'Layered stratification',
    techniqueSteps: [
      'Select dentin opacity one shade deeper than the body shade.',
      'Build the palatal enamel shell first, then the dentin core.',
      'Leave space for a facial enamel layer of 0.5 mm.',
      'Finish with contour, then gloss — texture before polish.'
    ],
    productId: 'supreme',
    accent: brand.supreme,
    image: imagery.anterior,
    evidence:
      '10-year clinical follow-up on nanofiller composite restorations shows retained gloss and marginal integrity.'
  },
  {
    id: 'everyday',
    index: '02',
    label: 'Everyday simplicity',
    demand: 'Most of the week is not a complex case. It is the same restoration, repeated — and the shade guide still slows you down.',
    resolution: 'Three shade options instead of thirty, for anterior and posterior alike.',
    technique: 'Single-shade selection',
    techniqueSteps: [
      'Assess the tooth wet, before isolation, under neutral light.',
      'Choose Light, Medium or Dark — no secondary shade mapping.',
      'Place in increments and sculpt directly.',
      'Polish and reassess hydrated — the shade settles into its surroundings.'
    ],
    productId: 'easy-match',
    accent: brand.easyMatch,
    image: imagery.enamel,
    evidence:
      'Shade-matching studies report clinically acceptable matches across the everyday VITA range from three options.'
  },
  {
    id: 'posterior',
    index: '03',
    label: 'Posterior efficiency',
    demand: 'Deep, wide, hard to reach — and the next patient is already in the waiting room.',
    resolution: 'One increment to 5 mm. Fill, sculpt, cure, move on.',
    technique: 'Bulk placement',
    techniqueSteps: [
      'Isolate and establish the proximal matrix and wedge.',
      'Place a single increment up to 5 mm — no capping layer needed.',
      'Sculpt cusps and fissures before curing.',
      'Cure for 20 seconds per surface, then adjust the occlusion.'
    ],
    productId: 'bulk',
    accent: brand.bulk,
    image: imagery.posterior,
    evidence:
      'Low shrinkage stress chemistry supports 5 mm increments with marginal performance comparable to incremental placement.'
  },
  {
    id: 'handling',
    index: '04',
    label: 'Flow and adaptation',
    demand: 'The material feels stiff, the access is tight, and adaptation is fighting you.',
    resolution: 'Warm the composite you already use. Same material, easier hands.',
    technique: 'Pre-warmed placement',
    techniqueSteps: [
      'Load the syringe or capsule into the warmer before the appointment.',
      'Let it reach a consistent working temperature.',
      'Place immediately, in a single increment where the cavity allows.',
      'Adapt and sculpt while the viscosity is still low.'
    ],
    productId: 'warmer',
    accent: brand.warmer,
    image: imagery.flow,
    evidence:
      'Pre-warming composite lowers viscosity, improving cavity adaptation and reducing voids at the margin.'
  }
]

export const needById = (id?: NeedId): ClinicalNeed =>
  needs.find((need) => need.id === id) ?? needs[0]
